import {Button, GenericInput, Modal, ModalContent, ModalFooter, ModalTitle} from '@hm/fundamental'
import {mdiClose, mdiDeleteOutline} from '@mdi/js'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useDeleteRefractory} from '../hooks/mutations'
import {useCanDeleteRefractory} from '../hooks/useCanDeleteRefractory'

interface DeleteRefractoryCellProps {
  refractoryId: number
  refractoryName: string
  dataCy?: string
}

export const DeleteRefractoryCell: React.FC<DeleteRefractoryCellProps> = ({
  refractoryId,
  refractoryName,
  dataCy = 'delete-refractory'
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const canDeleteRefractory = useCanDeleteRefractory()
  const {mutate: deleteRefractory} = useDeleteRefractory()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [confirmationName, setConfirmationName] = useState('')
  const isNameConfirmed = confirmationName !== '' && confirmationName === refractoryName

  if (!canDeleteRefractory) {
    return null
  }

  return (
    <>
      <Button
        variant="tertiary"
        className="text-red-500"
        icon={mdiDeleteOutline}
        onClick={() => {
          setConfirmationName('')
          setIsConfirmOpen(true)
        }}
        size="small"
        square
        data-cy={`${dataCy}-btn`}
        title={t('refraCatalog.delete.buttonTitle')}
      />
      {isConfirmOpen && (
        <Modal
          open
          onClose={() => setIsConfirmOpen(false)}
          isExitEnabled={false}
          data-cy="delete-refractory-confirm-dialog"
        >
          <ModalTitle>{t('refraCatalog.delete.confirmTitle')}</ModalTitle>
          <ModalContent>
            <div className="flex flex-col gap-4">
              <div>{t('refraCatalog.delete.confirmBody', {refractoryName})}</div>
              <GenericInput
                aria-label={t('refraCatalog.delete.confirmNameLabel')}
                placeholder={t('refraCatalog.delete.confirmNamePlaceholder')}
                value={confirmationName}
                onChange={setConfirmationName}
                onPaste={(event) => event.preventDefault()}
                onDrop={(event) => event.preventDefault()}
                autoComplete="off"
                data-cy="delete-refractory-confirm-name"
              />
            </div>
          </ModalContent>
          <ModalFooter>
            <div className="flex justify-end gap-4">
              <Button
                variant="secondary"
                icon={mdiClose}
                onClick={() => setIsConfirmOpen(false)}
                data-cy="delete-refractory-cancel-button"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="negative"
                icon={mdiDeleteOutline}
                disabled={!isNameConfirmed}
                onClick={() => {
                  if (!isNameConfirmed) return
                  deleteRefractory({refractoryId})
                  setIsConfirmOpen(false)
                }}
                data-cy="delete-refractory-confirm-button"
              >
                {t('refraCatalog.delete.confirmCta')}
              </Button>
            </div>
          </ModalFooter>
        </Modal>
      )}
    </>
  )
}
